'use client';

export interface FileItem {
  id: string;
  filename?: string | null;
  url: string;
  mimeType?: string | null;
  createdAt: Date | string;
}

export interface FilesDisplayProps {
  files: FileItem[];
  emptyMessage?: string;
  t: (key: string) => string;
  className?: string;
}

export function FilesDisplay({
  files,
  emptyMessage,
  className = '',
  t,
}: FilesDisplayProps) {
  if (files.length === 0) {
    return (
      <div
        className={`flex justify-center items-center  bg-el-grey-100 rounded-4 p-2 min-h-14  ${className}`}
      >
        <p className="text-12 text-el-grey-600">{emptyMessage || '-'}</p>
      </div>
    );
  }

  return (
    <div
      className={`grid grid-cols-1 gap-2 bg-el-grey-100 rounded-4 p-2 ${className}`}
    >
      {files.map((file) => (
        <div
          key={file.id}
          className="flex items-center justify-between p-3 bg-el-grey-200  rounded-4"
        >
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18.53 9L13 3.47C12.8595 3.32931 12.6688 3.25018 12.47 3.25H8C7.27065 3.25 6.57118 3.53973 6.05546 4.05546C5.53973 4.57118 5.25 5.27065 5.25 6V18C5.25 18.7293 5.53973 19.4288 6.05546 19.9445C6.57118 20.4603 7.27065 20.75 8 20.75H16C16.7293 20.75 17.4288 20.4603 17.9445 19.9445C18.4603 19.4288 18.75 18.7293 18.75 18V9.5C18.7421 9.3116 18.6636 9.13309 18.53 9ZM13.25 5.81L16.19 8.75H13.25V5.81ZM16 19.25H8C7.66848 19.25 7.35054 19.1183 7.11612 18.8839C6.8817 18.6495 6.75 18.3315 6.75 18V6C6.75 5.66848 6.8817 5.35054 7.11612 5.11612C7.35054 4.8817 7.66848 4.75 8 4.75H11.75V9.5C11.7526 9.69811 11.8324 9.88737 11.9725 10.0275C12.1126 10.1676 12.3019 10.2474 12.5 10.25H17.25V18C17.25 18.3315 17.1183 18.6495 16.8839 18.8839C16.6495 19.1183 16.3315 19.25 16 19.25Z"
                  fill="currentcolor"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-12 font-medium text-el-grey-600 truncate max-w-100">
                {file.filename || 'Unknown file'}
              </p>
            </div>
          </div>
          <div className="flex items-center">
            <a
              href={`/api/download/${file.id}`}
              download={file.filename || undefined}
              className="text-el-grey-600  "
              title={t('actions.download') || 'Download'}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18.3974 13.7998C18.5567 13.7999 18.7096 13.8631 18.8222 13.9756C18.9347 14.088 18.9979 14.2405 18.998 14.3994V16.7998C19.022 17.3561 18.8252 17.8995 18.4501 18.3115C18.0751 18.7235 17.5519 18.971 16.9951 19H7.02827C6.7506 18.9886 6.47769 18.9223 6.22554 18.8057C5.97334 18.6889 5.74675 18.5236 5.55855 18.3193C5.37037 18.1151 5.22436 17.8759 5.12886 17.6152C5.03335 17.3545 4.99009 17.0772 5.0019 16.7998V14.3994C5.00201 14.2405 5.06515 14.088 5.17769 13.9756C5.29033 13.8631 5.4432 13.7999 5.60249 13.7998C5.7618 13.7998 5.91462 13.8631 6.0273 13.9756C6.13989 14.088 6.20297 14.2404 6.20308 14.3994V16.7998C6.18241 17.0373 6.25421 17.2738 6.40327 17.46C6.55244 17.6461 6.76821 17.7678 7.00483 17.7998H16.9951C17.2316 17.7678 17.4465 17.646 17.5957 17.46C17.7448 17.2738 17.8165 17.0373 17.7959 16.7998V14.3994C17.796 14.2404 17.86 14.088 17.9726 13.9756C18.0853 13.8632 18.2381 13.7998 18.3974 13.7998ZM12.0117 5C12.171 5 12.3238 5.06334 12.4365 5.17578C12.5491 5.28821 12.6131 5.44062 12.6132 5.59961V12.9502L14.7919 10.7754C14.9057 10.6696 15.0564 10.6126 15.2119 10.6152C15.3675 10.618 15.5168 10.6801 15.6269 10.79C15.737 10.9 15.7999 11.0487 15.8027 11.2041C15.8054 11.3595 15.7477 11.5103 15.6416 11.624L12.4365 14.8242C12.3811 14.8801 12.3149 14.9239 12.2421 14.9541C12.1692 14.9843 12.0906 15.0004 12.0117 15C11.9329 15.0003 11.855 14.9843 11.7822 14.9541C11.7094 14.924 11.6432 14.8801 11.5878 14.8242L8.38276 11.624C8.27663 11.5103 8.21888 11.3595 8.22163 11.2041C8.22439 11.0487 8.28734 10.9 8.39741 10.79C8.5074 10.6803 8.65602 10.6181 8.81148 10.6152C8.96713 10.6125 9.11847 10.6694 9.23237 10.7754L11.4111 12.9512V5.59961C11.4112 5.44067 11.4743 5.2882 11.5869 5.17578C11.6995 5.06331 11.8524 5.00006 12.0117 5Z"
                  fill="currentcolor"
                />
              </svg>
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}
